import { useRef } from "preact/hooks";

/**
 * Long-press via pointer events: fires after `holdMs` unless the pointer lifts or moves more than 10 px.
 * The click that follows a long-press is suppressed. Elements using this should also set
 * `-webkit-touch-callout: none` and `user-select: none` so iOS does not show the selection callout.
 */
export function useLongPress(action: () => void, holdMs = 600) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = () => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    start.current = null;
  };

  return {
    onPointerDown: (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => { timer.current = null; fired.current = true; action(); }, holdMs);
    },
    onPointerMove: (e: PointerEvent) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu: (e: Event) => e.preventDefault(),
    onClickCapture: (e: Event) => { if (fired.current) { fired.current = false; e.stopPropagation(); e.preventDefault(); } },
  };
}
